import React, { Suspense, useCallback, useState } from 'react'
import Tooltip from 'components/common/tooltip/Tooltip'
import { ArchiveIssueConfirmation } from 'components/canary/issue_tracking/ArchiveIssueConfirmation'
import { createPotentialIssue } from 'components/canary/issue_tracking/issueTrackingIssuesApi'
import { useArchiveIssueConfirmation } from 'components/canary/issue_tracking/useArchiveIssueConfirmation'
import { useTrackedIssue } from 'components/review_tiles/ReviewIssueContext'
import type { Review } from 'components/supplier_show/prop_shapes/review'

const IssueModal = React.lazy(async () => {
  const module = await import('components/canary/issue_tracking/IssueModal')
  return { default: module.IssueModal }
})

interface ReviewIssueTrackingCTAProps {
  review: Review
  issueTrackingEnabled: boolean
  onRaiseIssueClick?: () => void
  onActiveIssueClick?: () => void
  onIssueStatusChange?: () => void | Promise<void>
  variant?: 'default' | 'activeOnly'
}

export const ReviewIssueTrackingCTA: React.FC<ReviewIssueTrackingCTAProps> = ({
  review,
  issueTrackingEnabled,
  onRaiseIssueClick,
  onActiveIssueClick,
  onIssueStatusChange,
  variant = 'default'
}) => {
  const [issue, setIssue] = useTrackedIssue(review)
  const [openIssueId, setOpenIssueId] = useState<number | null>(null)
  const [isTracking, setIsTracking] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const canManageIssueTracking = issueTrackingEnabled && review.issue_tracking?.can_manage === true

  const handleIssueArchived = useCallback(async (): Promise<void> => {
    setIssue(null)
    setOpenIssueId(null)

    try {
      await onIssueStatusChange?.()
    } catch (error) {
      console.error('Failed to refresh after archiving issue', error)
    }
  }, [onIssueStatusChange, setIssue])

  const archiveIssueConfirmation = useArchiveIssueConfirmation({
    archiveErrorMessage: 'There was an issue archiving this issue.',
    onArchiveStart: () => setActionError(null),
    onArchived: handleIssueArchived
  })
  const { requestArchiveIssue, ...archiveConfirmation } = archiveIssueConfirmation

  const handleTrackIssue = async (): Promise<void> => {
    setIsTracking(true)
    setActionError(null)

    try {
      const createdIssue = await createPotentialIssue(review.id)
      const trackedIssue = { issue_id: createdIssue.issueId, status: 'active' as const }
      setIssue(trackedIssue)
      setOpenIssueId(trackedIssue.issue_id)
    } catch (_error) {
      setActionError('There was an issue tracking this review.')
    } finally {
      setIsTracking(false)
    }
  }

  const handleIssueResolved = async (issueId: number): Promise<void> => {
    setIssue({ issue_id: issueId, status: 'resolved' })

    try {
      await onIssueStatusChange?.()
    } catch (error) {
      console.error('Failed to refresh after resolving issue', error)
    }
  }

  if (!canManageIssueTracking) return null

  return (
    <span className='review-control review-issue-tracking-cta canary'>
      {issue == null && variant === 'default' && (
        <Tooltip
          className='canary-issue-tracking-tooltip'
          anchor={
            <button
              type='button'
              className='canary-issue-tracking-flag canary-issue-tracking-flag--raise'
              disabled={isTracking}
              aria-label='Raise Issue'
              onClick={() => {
                if (onRaiseIssueClick != null) {
                  onRaiseIssueClick()
                  return
                }

                void handleTrackIssue()
              }}
            >
              <i aria-hidden='true' className={isTracking ? 'far fa-spinner fa-spin' : 'fa-solid fa-flag'} />
            </button>
          }
          content='Raise Issue'
        />
      )}

      {issue?.status === 'active' && (
        <Tooltip
          className='canary-issue-tracking-tooltip'
          anchor={
            <button
              type='button'
              className='canary-issue-tracking-flag canary-issue-tracking-flag--active'
              aria-label='View Active Issue'
              onClick={() => {
                if (onActiveIssueClick != null) {
                  onActiveIssueClick()
                  return
                }

                setOpenIssueId(issue.issue_id)
              }}
            >
              <i aria-hidden='true' className='fa-solid fa-flag' />
            </button>
          }
          content='View Active Issue'
        />
      )}

      {issue?.status === 'resolved' && (
        <Tooltip
          className='canary-issue-tracking-tooltip'
          anchor={
            <button
              type='button'
              className='canary-issue-tracking-flag canary-issue-tracking-flag--resolved'
              aria-label='View Resolved Issue'
              onClick={() => setOpenIssueId(issue.issue_id)}
            >
              <i aria-hidden='true' className='fa-solid fa-flag' />
            </button>
          }
          content='View Resolved Issue'
        />
      )}

      {actionError != null && <span role='alert' className='review-issue-tracking-cta__error'>{actionError}</span>}

      {openIssueId != null && (
        <Suspense fallback={null}>
          <IssueModal
            issueId={openIssueId}
            onClose={() => setOpenIssueId(null)}
            onArchive={requestArchiveIssue}
            onResolved={async () => await handleIssueResolved(openIssueId)}
          />
        </Suspense>
      )}
      <ArchiveIssueConfirmation archiveConfirmation={archiveConfirmation} />
    </span>
  )
}
